import { useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateDemandReviewerDto,
  CreateDemandReviewerResponseDto,
  DemandReviewerDto,
  GetDemandReviewersResponseDto,
} from '@tempo-io/fim3-dto';
import type { AxiosInstance } from 'axios';
import { demandsQueryKeys } from '@/lib/query-keys/demands';
import { useErrorHandlingMutation } from './useErrorHandlingMutation';

export const useDemandReviewersApi = (httpClient: AxiosInstance) => {
  const getDemandReviewers = async (demandId: number): Promise<DemandReviewerDto[]> => {
    const { data } = await httpClient.get<GetDemandReviewersResponseDto>(
      `/demands/${demandId}/reviewers`,
    );
    return data.data;
  };

  const createDemandReviewer = async ({
    demandId,
    dto,
  }: {
    demandId: number;
    dto: CreateDemandReviewerDto;
  }): Promise<DemandReviewerDto> => {
    const { data } = await httpClient.post<CreateDemandReviewerResponseDto>(
      `/demands/${demandId}/reviewers`,
      dto,
    );
    return data.data;
  };

  const removeDemandReviewer = async ({
    demandId,
    reviewerRecordId,
  }: {
    demandId: number;
    reviewerRecordId: number;
  }): Promise<void> => {
    await httpClient.delete(`/demands/${demandId}/reviewers/${reviewerRecordId}`);
  };

  const queryClient = useQueryClient();
  const invalidate = (demandId: number) =>
    queryClient.invalidateQueries({ queryKey: demandsQueryKeys.reviewers(demandId) });

  const useGetReviewers = (demandId: number) =>
    useQuery({
      queryKey: demandsQueryKeys.reviewers(demandId),
      queryFn: () => getDemandReviewers(demandId),
      enabled: demandId > 0,
    });

  const useAddReviewer = () =>
    useErrorHandlingMutation({
      mutationFn: createDemandReviewer,
      onSuccess: (_data, variables) => invalidate(variables.demandId),
    });

  const useRemoveReviewer = () =>
    useErrorHandlingMutation({
      mutationFn: removeDemandReviewer,
      onSuccess: (_data, variables) => invalidate(variables.demandId),
    });

  return {
    getDemandReviewers,
    useGetReviewers,
    useAddReviewer,
    useRemoveReviewer,
  };
};
